import { Injectable, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { Permission, PermissionList, PermissionRole, PermissionRule } from 'src/api/v3/common';
import requests from 'src/api/v3/requests';
import { DataTableGetter } from '../company/components/data-table/data-table.component';
import { FilterCriteria } from '../company/pages/company-users/company-users.component';
import { DropdownItem } from '../ui/dropdown/dropdown.component';
import { AuthService } from './auth.service';
import { LoggerService } from './logger.service';
import { TagService } from './tag.service';

@Injectable({
  providedIn: 'root'
})
export class PermissionService implements OnDestroy {
  // Naudojamas tik taisyklių rodymui lentelėje, kai neatfiltruota.
  public readonly permissionsRules = new Map<number, PermissionRule>();
  // Naudojamas įvairioms random taisyklėms, kurios reikalingos kaip pagalbinės užkrautiems kitiems duomenims.
  public sessionStorage = new Map<number, PermissionRule>();
  public listStorage: DropdownItem<number>[] = [];
  public listBeingLoaded = false;
  public readonly allGeneralPermissions: (keyof PermissionList)[] = [
    'company_settings',
    'ipcom_settings',
    'reactions',
    'roles',
    'systems',
    'events',
    'tags',
    'users',
    'support_tickets',
    'global_settings',
    'panic_settings',
    'regions',
    'impersonation',
    'transfer_company',
    'transfer_device',
    'system',
    'dev_setup_templates',
  ];
  public readonly allSystemPermissions: (keyof PermissionList)[] = [
    'sys_advanced_settings',
    'sys_information',
    'sys_areas',
    'sys_zones',
    'sys_outputs',
    'sys_sensors',
    'sys_thermostats',
    'sys_users',
    'sys_receiver',
    'sys_synchronisation',
    'sys_transfer',
    'sys_device_transfer',
    'sys_reset_sensors',
    'sys_assistance',
    'sys_personal_permissions',
    'sys_transfer_to_company',
    'sys_notifications',
    'sys_cameras',
    'sys_events',
  ];
  public static readonly indexedPermissionKeys: (keyof PermissionList)[] = [
    'company_settings',
    'ipcom_settings',
    'reactions',
    'roles',
    'systems',
    'events',
    'tags',
    'users',
    'support_tickets',
    'global_settings',
    'panic_settings',
    'regions',
    'impersonation',
    'transfer_company',
    'transfer_device',
    'sys_advanced_settings',
    'sys_information',
    'sys_areas',
    'sys_zones',
    'sys_outputs',
    'sys_sensors',
    'sys_thermostats',
    'sys_users',
    'sys_receiver',
    'sys_synchronisation',
    'sys_transfer',
    'sys_device_transfer',
    'sys_reset_sensors',
    'sys_assistance',
    'sys_personal_permissions',
    'sys_transfer_to_company',
    'sys_notifications',
    'sys_cameras',
    'sys_events',
    'system',
    'dev_setup_templates',
  ];
  public static readonly indextedAccessLevels: (keyof Permission)[] = [
    'view',
    'create',
    'edit',
    'delete',
    'execute',
  ];
  public readonly allAdvancedPermissions: (keyof PermissionList)[] = [];
  public static readonly defaultGeneralUserRule: PermissionRule = {
    company_id: 0,
    default: true,
    description: '',
    id: 4,
    name: '',
    parent_id: null,
    permissions: {
      company_settings: { create: false, view: false, edit: false, delete: false, execute: false },
      ipcom_settings: { create: false, view: false, edit: false, delete: false, execute: false },
      reactions: { create: false, view: false, edit: false, delete: false, execute: false },
      roles: { create: false, view: false, edit: false, delete: false, execute: false },
      systems: { create: false, view: false, edit: false, delete: false, execute: false },
      events: { create: false, view: false, edit: false, delete: false, execute: false },
      tags: { create: false, view: false, edit: false, delete: false, execute: false },
      users: { create: false, view: false, edit: false, delete: false, execute: false },
      support_tickets: { create: false, view: false, edit: false, delete: false, execute: false },
      global_settings: { create: false, view: false, edit: false, delete: false, execute: false },
      panic_settings: { create: false, view: false, edit: false, delete: false, execute: false },
      regions: { create: false, view: false, edit: false, delete: false, execute: false },
      impersonation: { create: false, view: false, edit: false, delete: false, execute: false },
      transfer_company: { create: false, view: false, edit: false, delete: false, execute: false },
      transfer_device: { create: false, view: false, edit: false, delete: false, execute: false },
      sys_advanced_settings: { create: false, view: false, edit: false, delete: false, execute: false },
      sys_information: { create: false, view: false, edit: false, delete: false, execute: false },
      sys_areas: { create: false, view: false, edit: false, delete: false, execute: false },
      sys_zones: { create: false, view: false, edit: false, delete: false, execute: false },
      sys_outputs: { create: false, view: false, edit: false, delete: false, execute: false },
      sys_sensors: { create: false, view: false, edit: false, delete: false, execute: false },
      sys_thermostats: { create: false, view: false, edit: false, delete: false, execute: false },
      sys_users: { create: false, view: false, edit: false, delete: false, execute: false },
      sys_receiver: { create: false, view: false, edit: false, delete: false, execute: false },
      sys_synchronisation: { create: false, view: false, edit: false, delete: false, execute: false },
      sys_transfer: { create: false, view: false, edit: false, delete: false, execute: false },
      sys_device_transfer: { create: false, view: false, edit: false, delete: false, execute: false },
      sys_reset_sensors: { create: false, view: false, edit: false, delete: false, execute: false },
      sys_assistance: { create: false, view: false, edit: false, delete: false, execute: false },
      sys_personal_permissions: { create: false, view: false, edit: false, delete: false, execute: false },
      sys_transfer_to_company: { create: false, view: false, edit: false, delete: false, execute: false },
      sys_notifications: { create: false, view: false, edit: false, delete: false, execute: false },
      sys_cameras: { create: false, view: false, edit: false, delete: false, execute: false },
      sys_events: { create: false, view: false, edit: false, delete: false, execute: false },
      system: { create: true, view: false, edit: false, delete: false, execute: false },
      dev_setup_templates: { create: false, view: false, edit: false, delete: false, execute: false},
    },
    role: PermissionRole.GeneralUser,
    tags: [],
  };
  private filterCriteria: FilterCriteria = null;
  private previousFilter: string = null;
  private tag = 'PermissionService';
  private cleanUpSubscriber: Subscription;

  constructor(private l: LoggerService, auth: AuthService) {
    this.cleanUpSubscriber = auth.onAccountOrRegionChnage.subscribe(() => {
      this.clear();
    });
  }

  ngOnDestroy() {
    this.cleanUpSubscriber?.unsubscribe();
  }

  public ingestPermissionRule(rule: PermissionRule) {
    if ( this.permissionsRules.has(rule.id) ) { return; }
    this.l.log('Ingesting rule ' + rule.id, this.tag, { rule });
    rule.tags?.forEach((t) => { t.textColor = TagService.getTextColor(t.color); });
    this.permissionsRules.set(rule.id, rule);
  }

  public ingestIntoSessionStorage(rule: PermissionRule) {
    if ( this.permissionsRules.has(rule.id) ) { return; } // Nedubliuojam
    if ( this.sessionStorage.has(rule.id) ) { return; }
    this.l.log('Ingesting rule [Session] ' + rule.id, this.tag, { rule });
    rule.tags?.forEach((t) => { t.textColor = TagService.getTextColor(t.color); });
    this.sessionStorage.set(rule.id, rule);
  }

  public getPermission(id: number): PermissionRule | undefined {
    return this.permissionsRules.get(id) ?? this.sessionStorage.get(id);
  }

  public async getPermissionAsync(id: number): Promise<PermissionRule | undefined> {
    const found = this.permissionsRules.get(id);
    if ( found ) { return found; }
    if ( id === 0 ) { return undefined; }

    await this.loadPermissionById(id);
    return this.permissionsRules.get(id);
  }

  public async loadPermissionById(id: number): Promise<PermissionRule> {
    try {
      const response = await requests.permission.getPermissionRule({id}).toPromise();
      this.ingestIntoSessionStorage(response.rule);
      // Tuo atveju jeigu taisyklė nauja, tai ją pridedam ir į listStorage
      if ( !this.listStorage.find(li => li.value === id) ) {
        this.listStorage.push({
          value: response.rule.id,
          label: response.rule.name,
          extraParam: response.rule.role,
        });
      }
      return response.rule;
    } catch (e) {
      console.log(e);
    }
  }

  public static rulesAreEqual(rule1: PermissionList, rule2: PermissionList): boolean {
    const keys = Object.keys(rule1);
    for ( const iKey of keys ) {
      const p1: Permission = rule1[iKey];
      const p2: Permission = rule2[iKey];
      if (
        p1.view !== p2.view
        || p1.create !== p2.create
        || p1.delete !== p2.delete
        || p1.edit !== p2.edit
        || p1.execute !== p2.execute
      ) {
        return false;
      }
    }

    return true;
  }

  public getDefaultPermissionFor(role: PermissionRole, companyId: number): PermissionRule {
    const foundByCompany = [...this.permissionsRules.values(), ...this.sessionStorage.values()].find(r => r.default && r.role === role && r.company_id === companyId);
    if ( foundByCompany ) { return foundByCompany; }
    return [...this.permissionsRules.values(), ...this.sessionStorage.values()].find(r => r.default && r.role === role && r.company_id === 0);
  }

  public static getCopyOf(list: PermissionList): PermissionList | undefined {
    if ( !list ) { return undefined; }
    const keys = Object.keys(list);
    const result = { ...list };
    for ( const iKey of keys ) {
      result[iKey] = { ...list[iKey] };
    }
    return result;
  }

  public static getCopyOfRule(rule: PermissionRule): PermissionRule | undefined {
    if ( !rule ) { return undefined; }
    const result = { ...rule };
    result.permissions = PermissionService.getCopyOf(rule.permissions);
    result.tags = [ ...rule.tags ];
    return result;
  }

  public getPermissionsGetter(filterCriteria: FilterCriteria | null): DataTableGetter<PermissionRule> {
    const permissionService = this;
    if ( filterCriteria ) {
      if (
        !this.filterCriteria
        || this.filterCriteria.searchPhrase !== filterCriteria.searchPhrase
        || JSON.stringify(filterCriteria.searchFields) !== JSON.stringify(this.filterCriteria.searchFields)
      ) {
        this.permissionsRules.clear();
      }
      this.previousFilter = JSON.stringify(this.filterCriteria);
      this.filterCriteria = JSON.parse(JSON.stringify(filterCriteria));
    } else if ( this.filterCriteria !== null ) {
      this.filterCriteria = null;
      this.permissionsRules.clear();
    }
    return async (current, columns, more) => {
      this.l.log('Load permission rules', '', { current, more, f: permissionService.filterCriteria, pf: permissionService.previousFilter });
      if ( !more ) { // data table nori atsinaujinti duomenis is atminties.
        if ( permissionService.permissionsRules.size > 0 ) {
          return [...permissionService.permissionsRules.values()];
        }
        if ( JSON.stringify(permissionService.filterCriteria) === permissionService.previousFilter ) {
          return [...permissionService.permissionsRules.values()];
        }
      }
      permissionService.previousFilter = JSON.stringify(permissionService.filterCriteria);
      const result = await requests.permission.getPermissionRules(filterCriteria ? {
        searchPhrase: filterCriteria.searchPhrase,
        searchFields: filterCriteria.searchFields,
        paginationPage: filterCriteria.currentPage + 1,
      } : null).toPromise();
      if ( !permissionService.filterCriteria ) {
        permissionService.filterCriteria = {
          searchPhrase: '',
          searchFields: [],
          currentPage: result.rules.current_page,
          lastPage: result.rules.last_page,
        };
        permissionService.previousFilter = JSON.stringify(permissionService.filterCriteria);
        if ( result.rules.total === permissionService.permissionsRules.size ) {
          permissionService.filterCriteria.currentPage = result.rules.last_page;
        }
      } else {
        permissionService.filterCriteria.currentPage = result.rules.current_page;
        permissionService.filterCriteria.lastPage = result.rules.last_page;
        permissionService.previousFilter = JSON.stringify(permissionService.filterCriteria);
      }
      result.rules.data.forEach((p) => permissionService.ingestPermissionRule(p));
      return [...permissionService.permissionsRules.values()];
    };
  }

  public clearFilter() {
    this.filterCriteria = null;
  }

  public async getAllPermissions() {
    console.log('load rules');
    const result = await requests.permission.getPermissionRules({}).toPromise();
    result.rules.data.forEach((p) => this.ingestPermissionRule(p));
    result.built_in_default.forEach((pd) => this.ingestIntoSessionStorage(pd));
    console.log('loaded ' + this.permissionsRules.size);
  }

  public removePermissionRule(id: number) {
    this.permissionsRules.delete(id);
    this.sessionStorage.delete(id);
    const foundInList = this.listStorage.find(i => i.value === id);
    if ( foundInList ) {
      this.listStorage.splice(this.listStorage.indexOf(foundInList), 1);
    }
  }

  public clear() {
    this.permissionsRules.clear();
    this.sessionStorage.clear();
    this.listStorage = [];
    this.filterCriteria = null;
    this.previousFilter = null;
  }

  public async loadDropDownList(): Promise<void> {
    if ( this.listStorage.length > 0 ) { return; }
    if ( this.listBeingLoaded ) { return; }
    this.listBeingLoaded = true;
    try {
      const result = await requests.permission.getPermissionRules({ forList: true }).toPromise();
      result.rules.data.forEach(r => this.listStorage.push({
        value: r.id,
        label: r.name,
        extraParam: r.role,
      }));
    } finally {
      this.listBeingLoaded = false;
    }
  }

  public static checkPermissionInAString(permissions: string, permissionKey: keyof PermissionList, accessLevel: keyof Permission): boolean {
    const index = PermissionService.indexedPermissionKeys.indexOf(permissionKey);
    if ( index === -1 ) { return false; }
    if ( index*2 + 2 > permissions.length ) { return false; }
    const accessIndex = PermissionService.indextedAccessLevels.indexOf(accessLevel);
    if ( accessIndex === -1 ) { return false; }
    if ( accessIndex + 1 > permissions.length ) { return false; }
    const byteStr = permissions.substring(index, index + 2);
    const byte = parseInt(byteStr, 16);
    // eslint-disable-next-line no-bitwise
    return (byte & (1 << accessIndex)) !== 0;
  }
}
